import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { Event } from '../../../../../typings/Event.interface';
import { Request } from '../../../../../typings/Request.interface';
import ReplayTargetFields, { ReplayTargetFormValues, formatReplayTarget, validateReplayTarget } from '../common/BulkOperations/ReplayTargetFields';
import { useDialog } from '../common/Dialog';
import { useToasts } from '../common/Toast';
import { GlobalContext } from '../contexts/GlobalContext';
import { useIsQuotaEnforcementActive } from './useIsQuotaEnforcementActive';
export type RequestActionResult = {
  requests: Request[];
  events: Event[];
};

/**
 * Replay entry point for a single request: re-ingests the request through the
 * pipeline as a brand new request, targeting a source and either all of that
 * source's connections or a specific subset. The original request and events
 * are untouched. (Retry lives elsewhere — this dialog is replay-only.)
 */
const useRequestActions = callback => {
  const $ = _c(6);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const is_quota_enforcement_active = useIsQuotaEnforcementActive();
  let t0;
  if ($[0] !== HookdeckAPI || $[1] !== addToast || $[2] !== callback || $[3] !== is_quota_enforcement_active || $[4] !== showDialog) {
    t0 = {
      openActionDialog: async request_arg => {
        if (is_quota_enforcement_active) {
          addToast("error", "Action disabled while plan is exceeded. Please upgrade to continue.");
          return;
        }
        await new Promise(resolve => {
          showDialog(async raw_values => {
            const values = raw_values as ReplayTargetFormValues;
            await HookdeckAPI.requests.replay(request_arg.id, formatReplayTarget(values)).then(result => {
              const event_count = result.events?.length ?? 0;
              const request_count = result.requests?.length ?? 0;
              addToast("success", `Replay created: ${event_count} new event(s) across ${request_count} request(s)`);
              if (callback) {
                callback(request_arg, {
                  requests: result.requests,
                  events: result.events
                });
              }
            }).catch(error => {
              if (error.response?.code === "DATA_ARCHIVED") {
                addToast("error", "Failed to replay: request data is archived");
              } else {
                addToast("error", "Failed to replay request");
              }
            });
            resolve(null);
          }, () => resolve(null), {
            title: "Replay request",
            submit_label: "Run",
            submit_icon: "replay",
            cancel_label: "Cancel",
            form_props: {
              initial_values: {
                source_id: request_arg.source_id,
                connection_scope: "all",
                webhook_ids: []
              } as ReplayTargetFormValues,
              validate: _temp,
              Fields: _temp2
            }
          });
        });
      }
    };
    $[0] = HookdeckAPI;
    $[1] = addToast;
    $[2] = callback;
    $[3] = is_quota_enforcement_active;
    $[4] = showDialog;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
};
export default useRequestActions;
function _temp(raw_values_0) {
  return validateReplayTarget(raw_values_0 as ReplayTargetFormValues);
}
function _temp2() {
  return <ReplayTargetFields />;
}